import LoadingContent from "@/components/common/LoadingContent";
import { getNumberWithOrdinal } from "@/components/common/validation/Validators";
import SetUpModeCompany from "@/components/employees/SetUpModeCompany";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { getPeriodStatus } from "@/store/actions/periodStatus";
import { Actions as ALLOWANCE_ACTIONS } from "../../store/actions/allowanceActions";
import { Actions as EMPLOYEEACTIONS } from "../../store/actions/employeeActions";
import { Actions as PAYROLL_ACTIONS } from "../../store/actions/payrollActions";
import { Actions as PERMISSION_ACTIONS } from "../../store/actions/permissionActions";
import ArchivedCompany from "../company-pages/ArchivedCompany";
import ColaRequired from "../company-pages/ColaRequired";
import EnrolmentRequired from "../company-pages/EnrolmentRequired";
import InvalidPermissions from "../company-pages/InvalidPermissions";
import UpgradeRequired from "../company-pages/UpgradeRequired";
import InviteEmployeeModal from "../employees/InviteEmployeeModal";
import HolidayPayModal from "./HolidayPayModal";
import PayTerminatedEmployeeModal from "./PayTerminatedEmployeeModal";
import PayrollComponent from "./Payroll";
import PayrollPeriod from "./PayrollPeriod";
import ResetPeriodStartModal from "./ResetPeriodStartModal";
import RunPayroll from "./RunPayroll";
import UnprocessTimesheetModal from "./UnprocessTimesheetModal";
import CompletingPayroll from "./pages/CompletingPayroll";
import Concluded from "./pages/Concluded";
import PayrollApproval from "./pages/PayrollApproval";
import PayrollAwaitingConfirmation from "./pages/PayrollAwaitingConfirmation";
import PayrollClientIssues from "./pages/PayrollClientIssues";
import PayrollInProgress from "./pages/PayrollInProgress";
import Summary from "./pages/PayrollSummary";
import UploadAllowanceModal from "./tools/UploadAllowanceModal";
import PayrollDataChecklist from "./pages/PayrollDataChecklist";
import DataSubmitted from "./pages/DataSubmitted";
import {addMonths, isBefore, set} from "date-fns";
import LateSubmitModal from "./modals/LateSubmitModal";

const BASIC_PLAN = "f73c2c10-aa39-4408-96e3-a68915bb9d3b";
const PARTNER_UNLIMITED = "89db58e5-c640-4dce-a329-097b61029194";
const ADVISOR_ONLY = "c01a916c-31d4-4e50-8d80-8e36e220be39";

const PayrollPage = ({
	periodStatus,
	managedByAdvisor,
	periodIsUpdating,
	clientReviewsPayroll,
	userIsAdvisor,
	clientEntersPayrollData,
	adminsCanProcessPayroll,
	handleConclude,
}) => {
	const userIsClient = !managedByAdvisor || !userIsAdvisor;
	const userEntersData = userIsClient && clientEntersPayrollData;

	if (periodIsUpdating) return <CompletingPayroll generatingReports={periodIsUpdating} />;
	if (periodStatus === "Data Submitted" && !userIsAdvisor) return <DataSubmitted />;
	if (periodStatus === "Open" && managedByAdvisor && !userEntersData && !userIsAdvisor && !adminsCanProcessPayroll)
		return <PayrollInProgress />;
	if (periodStatus === "Open" && userEntersData && !userIsAdvisor) return <PayrollDataChecklist />;
	if (["Data Submitted", "Open"].includes(periodStatus))
		return (
			<>
				<PayrollComponent />
				<RunPayroll />
			</>
		);
	if (periodStatus === "Rejected") return <PayrollClientIssues handleConclude={handleConclude} />;
	if (periodStatus === "Approved") return <Summary handleConclude={handleConclude} />;
	if (periodStatus === "Awaiting Approval" && clientReviewsPayroll && userIsAdvisor)
		return <PayrollAwaitingConfirmation handleConclude={handleConclude} />;
	if (periodStatus === "Awaiting Approval" && clientReviewsPayroll && !userIsAdvisor) return <PayrollApproval />;
	if (periodStatus === "Concluded") return <Concluded />;

	return null;
};

const Payroll = ({
	userPermissions,
	requiresCola,
	referenceDate,
	getCola,
	getAutoEnrolment,
	requiresEnrolment,
	requiresReEnrolment,
	jurisdiction,
	hasAccessToPayroll,
	clearPayrollState,
	companyStatus,
	period,
	paySchedule,
	payrollGetPayPeriod,
	payrollGetList,
	getMyPermissions,
	allowanceGetList,
	updatingPayPeriod,
	payPeriodsUpdating,
	getPayPeriodDetails,
	clientReviewsPayroll,
	clientEntersPayrollData,
	userIsAdvisor,
	managedByAdvisor,
	adminsCanProcessPayroll,
	onlyAdvisor,
	isAdmin,
	openLateSubmitModal,
	concludePayroll,
}) => {
	const { payPeriodId: periodId } = useParams();

	const periodIsUpdating = useMemo(
		() => Object.keys({ ...payPeriodsUpdating }).includes(periodId),
		[JSON.stringify(payPeriodsUpdating)],
	);

	useEffect(() => {
		if (!period && !updatingPayPeriod) getPayPeriodDetails(periodId);
	}, [JSON.stringify(period), periodId]);

	const status = useMemo(() => {
		const periodStatus = getPeriodStatus(period);
		return periodStatus;
	}, [periodId, period?.status, period?.complete]);

	useEffect(() => {
		if (periodId) {
			allowanceGetList();
			getMyPermissions();
			payrollGetList(periodId);
		}
	}, [periodId, period?.status]);

	// useEffect(() => {
	// 	if (!updatingPayPeriod && payScheduleId) {
	// 		payrollGetPayPeriod({ paySchedule: payScheduleId });
	// 	}
	// }, [payScheduleId]);

	useEffect(() => {
		if (["MT"].includes(jurisdiction)) getCola();
		if (["UK"].includes(jurisdiction) && period?.id) {
			const { periodStart, periodEnd } = period || {};
			getAutoEnrolment(period?.id, periodStart, periodEnd);
		}
	}, [period?.id]);

	useEffect(() => {
		if (period)
			return () => {
				clearPayrollState();
			};
	}, []);

	const handleConclude = () => {
		//If the payDay is in the past and there is no late reason, open the late submit modal
		if (isBefore(set(new Date(period?.payDay), { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 }), new Date()) && !period.lateReason && jurisdiction == "UK")
			return openLateSubmitModal();

		concludePayroll(period.id);
	};

	const periodOrdinal = getNumberWithOrdinal(period?.period);

	const periodRequiresClientApproval = clientReviewsPayroll && !userIsAdvisor;

	if (!period || !periodOrdinal || updatingPayPeriod) return <LoadingContent />;
	if (companyStatus == "Archived") return <ArchivedCompany />;
	if (companyStatus == "Setup") return <SetUpModeCompany />;
	if (!isAdmin && ((periodRequiresClientApproval ? false : !hasAccessToPayroll) || (onlyAdvisor && !userIsAdvisor))) {
		return <UpgradeRequired />;
	}
	if (requiresCola) return <ColaRequired year={referenceDate} />;
	if (requiresReEnrolment) return <EnrolmentRequired reEnrolment={true}/>;
	if (requiresEnrolment) return <EnrolmentRequired />;
	if (userPermissions == "None" || userPermissions == "Read") return <InvalidPermissions />;

	return (
		<>
			<ResetPeriodStartModal />
			<UnprocessTimesheetModal />
			<PayTerminatedEmployeeModal />
			<InviteEmployeeModal />
			<HolidayPayModal />
			<UploadAllowanceModal />
			<LateSubmitModal payPeriodDetails={period} />

			<div className={`content ${status === "Open" ? "bg-gray-lighter" : ""}`}>
				<div className="row pb-0">
					<div className="col-xs-12">
						<div className="pull-left">
							<h1 className="page-heading">
								Run Payroll - {periodOrdinal} {paySchedule?.name} of {period?.year}
							</h1>
						</div>
					</div>
				</div>
				{["Data Submitted", "Open"].includes(status) && (
					<div className="row pt-0 pb-0">
						<div className="col-xs-12">
							<PayrollPeriod disabled={managedByAdvisor && !(userIsAdvisor || adminsCanProcessPayroll)} />
						</div>
					</div>
				)}
				<div className="clearfix" />
			</div>
			<div className="content mb-32">
				<Routes>
					<Route
						path="/"
						element={
							<PayrollPage
								{...{
									periodStatus: status,
									periodIsUpdating,
									isAdvisor: false,
									clientReviewsPayroll,
									userIsAdvisor,
									managedByAdvisor,
									clientEntersPayrollData,
									adminsCanProcessPayroll,
									handleConclude,
								}}
							/>
						}
					/>
					{["Data Submitted", "Open"].includes(status) &&
						!periodIsUpdating &&
						(managedByAdvisor ? userIsAdvisor || adminsCanProcessPayroll : true) && (
							<Route
								path="/summary"
								element={<Summary handleConclude={handleConclude} />}
							/>
						)}
					{status === "Open" && (managedByAdvisor ? !(userIsAdvisor || adminsCanProcessPayroll) : false) && (
						<Route
							path="/data-entry"
							element={<RunPayroll />}
						/>
					)}
					<Route
						path="*"
						element={
							<Navigate
								to=""
								replace
							/>
						}
					/>
				</Routes>
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	const payPeriodDetails = state.payroll?.payPeriodDetails;
	const { paySchedules } = state.company?.settings;

	const paySchedule = paySchedules?.find((schedule) => schedule.id === payPeriodDetails?.paySchedule);

	const referenceDate = state.cola?.referenceDate;
	const isAfterPeriodStart = referenceDate < payPeriodDetails.periodStart;
	const isAfterPeriodEnd = referenceDate < payPeriodDetails.periodEnd;
	const requiresCola =
		state?.company?.settings?.jurisdiction == "MT" &&
		state.cola?.colaRequired &&
		(isAfterPeriodEnd || isAfterPeriodStart);

	const requiresEnrolment =
		state.notifications?.list?.filter((item) => {
			const isAutoEnrolment = item.source == "Auto Enrolment";
			const startIsBeforePeriodEnd = item.entry?.newDate <= payPeriodDetails.periodEnd;
			const startIsAfterPeriodStart = item.entry?.newDate >= payPeriodDetails.periodStart;
			return isAutoEnrolment && startIsBeforePeriodEnd && startIsAfterPeriodStart;
		})?.length > 0;

	const reassessmentDateEnd = addMonths(new Date(state?.pensions?.reassessmentDate),3);
	const reassessmentEmployees = state?.pensions?.reassessmentEmployees;
	const requiresReEnrolment = (isBefore(reassessmentDateEnd, new Date()) /*&& reassessmentEmployees?.length > 0*/) || false;

	const userPermissions = state?.permissions?.my?.permissions?.payroll;

	const { plan: companyPlan, isDemo } = state?.company?.settings || {};

	const companyIsManagedByAdvisor = state?.company?.settings?.advisor === state?.my?.profile?.advisorId;
	const advisorPlan = state?.my?.profile?.advisorPlan;
	const companyHasAccess = state.permissions?.plan?.payroll || isDemo;
	// advisor has access is a fallback
	const advisorHasAccess = companyIsManagedByAdvisor && advisorPlan == PARTNER_UNLIMITED ? true : companyHasAccess;

	const { isAdmin } = state?.my?.profile || {};

	const managedByAdvisor = !!state?.company?.settings?.advisor;
	const clientReviewsPayroll = state?.company?.settings?.clientReviewsPayroll && state.company?.settings?.advisor;
	const clientEntersPayrollData = state?.company?.settings?.clientEntersPayrollData;
	const userIsAdvisor = state?.my?.profile?.advisorId === state.company?.settings?.advisor || isAdmin;
	const onlyAdvisor = state?.company?.settings?.plan === ADVISOR_ONLY;

	return {
		userPermissions,
		requiresCola,
		requiresEnrolment,
		requiresReEnrolment,
		jurisdiction: state?.company?.settings?.jurisdiction,
		referenceDate: referenceDate?.substring(0, 4),
		payPeriodId: state.payroll?.payPeriodDetails?.id,
		period: state.payroll?.payPeriodDetails,
		updatingPayPeriod: state.payroll.updatingPayPeriod,
		payPeriodsUpdating: state.payPeriods?.updating,
		companyStatus: state?.company?.settings?.status,
		hasAccessToPayroll: advisorHasAccess || companyHasAccess,
		paySchedule,
		clientReviewsPayroll,
		clientEntersPayrollData,
		adminsCanProcessPayroll: state?.company?.settings?.adminsCanProcessPayroll,
		userIsAdvisor,
		managedByAdvisor,
		onlyAdvisor,
		isAdmin: state.my.profile.isAdmin,
	};
};

const mapDispatchToProps = (dispatch) => ({
	clearPayrollState: () => dispatch(PAYROLL_ACTIONS.clearPayrollState()),
	payrollGetPayPeriod: (options) => dispatch(PAYROLL_ACTIONS.payrollGetPayPeriod(options)),
	getCola: () => dispatch(EMPLOYEEACTIONS.getCola()),
	payrollGetList: (payPeriodId) => dispatch(PAYROLL_ACTIONS.payrollGetList(payPeriodId)),
	getMyPermissions: () => dispatch(PERMISSION_ACTIONS.getMyPermissions()),
	allowanceGetList: () => dispatch(ALLOWANCE_ACTIONS.allowanceGetList()),
	getPayPeriodDetails: (id) => dispatch(PAYROLL_ACTIONS.getPayPeriodDetails(id)),
	getAutoEnrolment: (payPeriodId, startDate, endDate) =>
		dispatch(EMPLOYEEACTIONS.getAutoEnrolment(payPeriodId, { startDate, endDate })),
	openLateSubmitModal: () => dispatch(PAYROLL_ACTIONS.setLateSubmitModal(true)),
	concludePayroll: (id) => dispatch(PAYROLL_ACTIONS.concludePayroll({ id })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Payroll);
